import { brand } from "@tactical-agent-neural-knowledge/sdk/design";

/**
 * The bridge inside the running application.
 *
 * A Blueprint frame holds this app, live, inside an `<iframe>`. The board cannot
 * reach into that document to find out what somebody clicked, so the app reports it:
 * a click in capture phase, the nearest ancestor carrying the stamp the preview build
 * put there, and a `postMessage` out to the board.
 *
 * Two things make this safe enough to run against real data. It is only ever on when
 * the page is inside a frame *and* the server asked for it with `?board=1` — the flag
 * `internal/board/preview.go` appends and nothing else sets. And while it is on, a
 * click is swallowed rather than performed: the embedded app is the real product with
 * the real workspace in it, so clicking "Delete" has to inspect that button, never
 * delete anything.
 */

export const ELEMENT_MESSAGE = "tank:element";
export const PICK_MESSAGE = "tank:pick";
export const READY_MESSAGE = "tank:bridge";

/** What was clicked, as it crosses the frame boundary. Mirrors `tank.board.v1.Element`. */
export type ElementMessage = {
  type: typeof ELEMENT_MESSAGE;
  /** "src/features/shell/Logo.tsx:8:5", or "" when nothing on the way up was stamped. */
  source: string;
  tag: string;
  text: string;
  testId: string;
  classes: string[];
  /** Where it sits inside the frame's viewport, in CSS pixels. */
  rect: { x: number; y: number; w: number; h: number };
};

/** The board turning picking on and off. */
export type PickMessage = { type: typeof PICK_MESSAGE; on: boolean };

/** How much of an element's own words to carry. Enough to recognise it by, not a page. */
const TEXT_LIMIT = 140;

/**
 * Whether this document is a board frame.
 *
 * Both halves matter. Inside a frame but without the flag is the desktop shell or
 * somebody else's embed, and the flag outside a frame is a URL anyone can type.
 */
export function inBoardFrame(win: Window): boolean {
  try {
    if (win.parent === win) return false;
  } catch {
    // A cross-origin parent throws on comparison in some engines; being unable to see
    // the parent is itself proof there is one.
  }
  return new URLSearchParams(win.location.search).get("board") === "1";
}

/**
 * Origins allowed to drive the bridge, and to be posted to.
 *
 * The board and the preview are the same origin today — the server points a frame at
 * the deployed web build — so the app's own origin is the whole list. `VITE_BOARD_ORIGINS`
 * is there for the day a preview is served from somewhere else; a URL parameter could
 * never be trusted for this, because the page asking is the page being tested.
 */
export function allowedParentOrigins(ownOrigin: string, configured?: string): string[] {
  const extra = (configured ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return [...new Set([ownOrigin, ...extra])];
}

export function isTrustedOrigin(origin: string, allowed: string[]): boolean {
  // "null" is what a sandboxed or data: document reports, and it must never match.
  return origin !== "null" && origin !== "" && allowed.includes(origin);
}

/**
 * The nearest element on the way up that knows where it came from, the element itself
 * included.
 *
 * Most of this app's DOM is drawn by Material UI components rather than by raw tags, and
 * the stamp only lands on tags, so the answer is usually a few steps above the thing
 * that was clicked. That is why the payload carries both: the tag and words of what was
 * pointed at, and the source of the closest thing that admits to a file and a line.
 */
export function stampedAncestor(el: Element | null): Element | undefined {
  for (let node: Element | null = el; node; node = node.parentElement) {
    if (node.hasAttribute?.("data-tank-src")) return node;
  }
  return undefined;
}

/** What to say about an element: what it is, what it says, and where it came from. */
export function describeElement(el: Element): ElementMessage {
  const box = el.getBoundingClientRect();
  const stamped = stampedAncestor(el);
  return {
    type: ELEMENT_MESSAGE,
    source: stamped?.getAttribute("data-tank-src") ?? "",
    tag: el.tagName.toLowerCase(),
    text: elementText(el),
    testId: el.getAttribute("data-testid") ?? "",
    classes: [...el.classList],
    rect: {
      x: Math.round(box.left),
      y: Math.round(box.top),
      w: Math.round(box.width),
      h: Math.round(box.height),
    },
  };
}

/**
 * The words an element shows. An input has none, so its placeholder or its label
 * stands in; without that a person reading the board would have nothing to recognise.
 */
export function elementText(el: Element): string {
  const own = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  const stand =
    own ||
    el.getAttribute("aria-label") ||
    el.getAttribute("placeholder") ||
    el.getAttribute("title") ||
    el.getAttribute("alt") ||
    "";
  return stand.slice(0, TEXT_LIMIT);
}

/** A message from the board, once it has been proved to come from the board. */
export function parsePickMessage(data: unknown): PickMessage | undefined {
  if (!data || typeof data !== "object") return undefined;
  const m = data as { type?: unknown; on?: unknown };
  if (m.type !== PICK_MESSAGE) return undefined;
  return { type: PICK_MESSAGE, on: m.on !== false };
}

/**
 * Start reporting clicks to the board. Returns a function that stops everything,
 * including the listener that was waiting to be told to stop.
 *
 * A no-op unless this document really is a board frame, so calling it from the app's
 * entry point costs an ordinary page nothing.
 */
export function startBoardBridge(win: Window = window): () => void {
  if (!inBoardFrame(win)) return () => {};
  const doc = win.document;
  const allowed = allowedParentOrigins(win.location.origin, import.meta.env.VITE_BOARD_ORIGINS);
  // Where replies go. The board's own origin once it has spoken and been believed; the
  // app's own origin until then, which is where the board is served from today. Never
  // "*": this frame is signed in as a real person.
  let target = win.location.origin;
  let picking = true;
  let outline: HTMLDivElement | undefined;

  const hide = () => {
    outline?.remove();
    outline = undefined;
  };

  /** The light box around what is about to be picked, so nobody clicks blind. */
  const show = (el: Element) => {
    const box = el.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) return hide();
    if (!outline) {
      outline = doc.createElement("div");
      outline.setAttribute("data-tank-bridge", "outline");
      Object.assign(outline.style, {
        position: "fixed",
        pointerEvents: "none",
        zIndex: "2147483647",
        border: `1px solid ${brand.neuralNeonCyan}`,
        borderRadius: "2px",
        background: "rgba(0, 229, 255, 0.08)",
      });
      doc.body.appendChild(outline);
    }
    Object.assign(outline.style, {
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
    });
  };

  const onMove = (e: Event) => {
    if (!picking) return;
    const el = e.target;
    if (el instanceof Element) show(el);
  };

  const onLeave = () => hide();

  const onClick = (e: Event) => {
    if (!picking) return;
    const el = e.target;
    if (!(el instanceof Element)) return;
    // Capture phase, and nothing is allowed past: React's own listeners sit on the root
    // container below this, and the app behind them is the real one.
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation?.();
    win.parent?.postMessage(describeElement(el), target);
  };

  const onMessage = (e: MessageEvent) => {
    if (e.source !== win.parent || !isTrustedOrigin(e.origin, allowed)) return;
    const msg = parsePickMessage(e.data);
    if (!msg) return;
    target = e.origin;
    picking = msg.on;
    if (!picking) hide();
  };

  // Capture everywhere, because a click handler on the way down is the only one that
  // runs before the app's own.
  const opts = { capture: true } as const;
  doc.addEventListener("click", onClick, opts);
  doc.addEventListener("pointermove", onMove, opts);
  doc.addEventListener("pointerleave", onLeave, opts);
  win.addEventListener("message", onMessage);
  win.parent?.postMessage({ type: READY_MESSAGE, ready: true }, target);

  return () => {
    doc.removeEventListener("click", onClick, opts);
    doc.removeEventListener("pointermove", onMove, opts);
    doc.removeEventListener("pointerleave", onLeave, opts);
    win.removeEventListener("message", onMessage);
    hide();
  };
}
